import type { User } from "../features/user/types";
import { planBaseMaxAccounts } from "../features/upsell/constants/plans";
import { isUserPlan } from "../features/upsell/plan-utils";
import type { SkedPlanCodeName } from "../features/upsell/types";
import { getEntitlementNumber } from "./has-entitlement";
import { isReverseTrial } from "./reverse-trial";

/**
 * A plan limit can be either a concrete number (e.g., 5 accounts) or the
 * sentinel "unlimited" which we display specially during reverse trial.
 */
export type PlanLimit = number | "unlimited";

// Mirrors apps/schedugram-server/scripts/chargebee-plan-entitlements.json.
const CURRENT_PLAN_QUOTAS: Record<
  SkedPlanCodeName,
  { accounts: PlanLimit; users: PlanLimit }
> = {
  "sked-fundamentals": { accounts: 1, users: 1 },
  "sked-essentials": { accounts: 6, users: 3 },
  "sked-professional": { accounts: 10, users: 6 },
  "sked-enterprise": { accounts: 10, users: 6 },
  "sked-enterprise-custom": { accounts: "unlimited", users: "unlimited" },
};

/**
 * The Chargebee plan item id, read from the subscription's plan line with the
 * currency and period stripped (as the server's resolveCurrentPlanId does).
 * user.plan alone is not enough: the server truncates it to two dash
 * segments, so Custom arrives as "sked-enterprise" and the legacy plans as
 * the current ids.
 */
const resolvePlanItemId = (user: User): string | undefined => {
  const itemPriceId = user.subscription?.subscription?.subscription_items?.find(
    (item) => item.item_type === "plan"
  )?.item_price_id;
  if (!itemPriceId) return user.plan;
  return itemPriceId.replace(
    /-[A-Z]{3}-(Monthly|Yearly|Every-\d+-(Months?|Years?))$/i,
    ""
  );
};

const getCurrentPlanQuotas = (user: User) => {
  const planId = resolvePlanItemId(user);
  return planId && Object.hasOwn(CURRENT_PLAN_QUOTAS, planId)
    ? CURRENT_PLAN_QUOTAS[planId as SkedPlanCodeName]
    : undefined;
};

/**
 * Returns the maximum number of social accounts the user can connect.
 *
 * Source of truth order:
 *  1. Reverse trial → "unlimited" (all features unlocked via entitlements)
 *  2. Entitlement value at preferences.entitlements["accounts"]
 *  3. Current plans: the Chargebee grid quota, by exact plan id
 *  4. Any other plan, including legacy: planBaseMaxAccounts[user.plan]
 *
 * The fallback exists for legacy paid customers whose backend hasn't yet been
 * migrated to entitlements. Remove when all accounts have entitlements set.
 */
export const getAccountLimit = (user: User | null | undefined): PlanLimit => {
  if (!user) return 0;
  if (isReverseTrial(user)) return "unlimited";
  const entitlementValue = getEntitlementNumber(user, "accounts");
  if (entitlementValue !== null && entitlementValue > 0)
    return entitlementValue;
  const current = getCurrentPlanQuotas(user);
  if (current) return current.accounts;
  const planKey = user.plan as SkedPlanCodeName;
  return planBaseMaxAccounts[planKey] ?? 0;
};

/**
 * Returns the maximum number of collaborators (users) the account can invite.
 *
 * Source of truth order:
 *  1. Reverse trial → "unlimited" (all features unlocked via entitlements)
 *  2. `maximum-users` entitlement — the Chargebee canonical key. Positive
 *     numeric values return as-is. `0` is treated as unknown/unhydrated
 *     (misconfigured accounts) and falls through to the plan-code fallback;
 *     consumers use the sentinel-0 treatment in users-list to avoid
 *     rendering "0 of 0 users". The literal string `"Unlimited"` (case /
 *     whitespace tolerant — used on the custom/enterprise-custom plan)
 *     returns the `"unlimited"` sentinel.
 *  3. Current plans: the Chargebee grid quota, by exact plan id.
 *  4. Any other plan, including legacy: the pre-rebrand plan-code match.
 *
 * The fallback exists for legacy paid customers whose backend hasn't yet
 * been migrated to entitlements. Remove when everyone has
 * `maximum-users` set.
 */
export const getUserLimit = (user: User | null | undefined): PlanLimit => {
  if (!user) return 0;
  if (isReverseTrial(user)) return "unlimited";
  const raw = user.preferences?.entitlements?.["maximum-users"];
  if (typeof raw === "string" && raw.trim().toLowerCase() === "unlimited") {
    return "unlimited";
  }
  const entitlementValue = getEntitlementNumber(user, "maximum-users");
  if (entitlementValue !== null && entitlementValue > 0)
    return entitlementValue;
  const current = getCurrentPlanQuotas(user);
  if (current) return current.users;
  if (isUserPlan(user, "sked-fundamentals")) return 1;
  if (isUserPlan(user, "sked-essentials")) return 1;
  if (isUserPlan(user, "sked-professional")) return 3;
  if (isUserPlan(user, "sked-enterprise")) return 6;
  return 0;
};

/**
 * Returns the maximum number of Social Listening competitors the user may
 * track. Entitlement-first with no plan-code fallback (the competitors feature
 * is already fully gated by the "listening-competitors" entitlement).
 */
export const getCompetitorLimit = (
  user: User | null | undefined
): PlanLimit => {
  if (!user) return 0;
  if (isReverseTrial(user)) return "unlimited";
  const entitlementValue = getEntitlementNumber(user, "listening-competitors");
  return entitlementValue ?? 0;
};

/**
 * Shared fallback for when the `maximum-bulk-posts` entitlement is absent.
 * This is the single source of truth — `getBulkCreateLimit` in
 * `features/post/bulk-create/limit-config.ts` imports this constant so UI
 * display and saga enforcement cannot drift.
 */
export const BULK_POSTS_DEFAULT_LIMIT = 20;

/**
 * Returns the maximum number of bulk posts a user may schedule per period.
 * Entitlement-driven via "maximum-bulk-posts"; falls back to
 * BULK_POSTS_DEFAULT_LIMIT to match the saga-enforced cap when the
 * entitlement isn't set.
 */
export const getBulkPostsLimit = (user: User | null | undefined): PlanLimit => {
  if (!user) return 0;
  if (isReverseTrial(user)) return "unlimited";
  const entitlementValue = getEntitlementNumber(user, "maximum-bulk-posts");
  return entitlementValue ?? BULK_POSTS_DEFAULT_LIMIT;
};

/**
 * Format a "X of Y" limit display string. During the reverse trial the
 * capacity side is rendered as "Unlimited during trial" to reinforce the
 * full-featured messaging.
 *
 * Examples:
 *   formatLimit(3, 5)             → "3 of 5"
 *   formatLimit(3, "unlimited")   → "3 of Unlimited during trial"
 *   formatLimit(3, "unlimited", { short: true }) → "Unlimited during trial"
 */
export const formatLimit = (
  current: number,
  limit: PlanLimit,
  options: { short?: boolean } = {}
): string => {
  if (limit === "unlimited") {
    return options.short
      ? "Unlimited during trial"
      : `${current} of Unlimited during trial`;
  }
  return `${current} of ${limit}`;
};

/**
 * True if the user is strictly at or above their limit (i.e., a further add
 * would exceed the quota). Reverse trial users are never "at limit".
 */
export const isAtLimit = (current: number, limit: PlanLimit): boolean => {
  if (limit === "unlimited") return false;
  return current >= limit;
};
